import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEntity } from '../../contexts/EntityContext';
import { api, type CashDesk, type CashDeskWallet, type CashDeskUser } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import Modal, { ConfirmModal } from '../../components/ui/Modal';

// ============================================================================
// CashDesksPage — Admin UI for managing cash desk groups
// ✅ RESOLVED: Variant A — cashiers in the same group see each other's data
// ✅ RESOLVED: Wallets must be explicitly assigned to a group to be visible to its cashiers
// ============================================================================

export default function CashDesksPage() {
  const { currentEntity } = useEntity();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [editDesk, setEditDesk] = useState<CashDesk | null>(null);
  const [deleteDesk, setDeleteDesk] = useState<CashDesk | null>(null);
  const [managingDesk, setManagingDesk] = useState<CashDesk | null>(null);
  const [newDeskName, setNewDeskName] = useState('');
  const [newDeskDesc, setNewDeskDesc] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['cash-desks', currentEntity?.id],
    queryFn: () => api.cashDesks.list(currentEntity!.id),
    enabled: !!currentEntity,
  });

  const { data: walletsData } = useQuery({
    queryKey: ['wallets', currentEntity?.id],
    queryFn: () => api.admin.wallets.list(currentEntity!.id),
    enabled: !!currentEntity && !!managingDesk,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.admin.users.list(),
    enabled: !!managingDesk,
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      api.cashDesks.create(currentEntity!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-desks'] });
      setShowCreate(false);
      setNewDeskName('');
      setNewDeskDesc('');
      success('Cash desk created');
    },
    onError: (e: any) => showError(e.message || 'Error creating cash desk'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: { name?: string; description?: string | null; isActive?: boolean } }) =>
      api.cashDesks.update(currentEntity!.id, id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-desks'] });
      setEditDesk(null);
      success('Cash desk updated');
    },
    onError: (e: any) => showError(e.message || 'Error updating cash desk'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.cashDesks.delete(currentEntity!.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-desks'] });
      setDeleteDesk(null);
      success('Cash desk deleted');
    },
    onError: (e: any) => showError(e.message || 'Error deleting cash desk'),
  });

  const assignWalletMutation = useMutation({
    mutationFn: ({ cashDeskId, walletId, assign }: { cashDeskId: string; walletId: string; assign: boolean }) =>
      assign
        ? api.cashDesks.assignWallet(currentEntity!.id, cashDeskId, walletId)
        : api.cashDesks.unassignWallet(currentEntity!.id, cashDeskId, walletId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-desks'] });
    },
    onError: (e: any) => showError(e.message || 'Error updating wallet assignment'),
  });

  const assignUserMutation = useMutation({
    mutationFn: ({ userId, cashDeskId }: { userId: string; cashDeskId: string | null }) =>
      api.cashDesks.assignUser(currentEntity!.id, userId, cashDeskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-desks'] });
      success('User assignment updated');
    },
    onError: (e: any) => showError(e.message || 'Error updating user assignment'),
  });

  const desks = data?.data ?? [];
  const allWallets: any[] = walletsData?.data ?? [];
  const allUsers: any[] = usersData?.data ?? [];

  // Use fresh desk data from query (managingDesk is stale local state after mutations)
  const freshManagingDesk = managingDesk ? desks.find((d: CashDesk) => d.id === managingDesk.id) ?? managingDesk : null;
  const assignedWalletIds = new Set((freshManagingDesk?.wallets ?? []).map((w: CashDeskWallet) => w.id));
  const assignedUserIds = new Set((freshManagingDesk?.users ?? []).map((u: CashDeskUser) => u.id));

  if (isLoading) {
    return <div className="loading-state"><div className="spinner" /></div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Группы касс</h1>
          <p className="page-subtitle" style={{ color: 'var(--color-subtext0)', marginTop: '0.25rem' }}>
            Разделение кассиров и кошельков по группам
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + Новая группа
        </button>
      </div>

      {/* Uncertainty notice */}
      <div className="info-notice" style={{
        background: 'var(--color-surface1)',
        border: '1px solid var(--color-yellow)',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        marginBottom: '1.5rem',
        color: 'var(--color-yellow)',
        fontSize: '0.85rem',
      }}>
        ⚠️ <strong>TODO/UNCLEAR:</strong> Видят ли Максим и Алексей транзакции друг друга внутри группы? (Вариант A: да) — уточнить у бизнеса.
      </div>

      {/* Desk cards */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {desks.length === 0 && (
          <div className="empty-state">Нет групп касс. Создайте первую.</div>
        )}

        {desks.map((desk: CashDesk) => (
          <div key={desk.id} className="card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{desk.name}</h3>
                  <span className={`badge ${desk.isActive ? 'badge-success' : 'badge-neutral'}`}>
                    {desk.isActive ? 'Активна' : 'Неактивна'}
                  </span>
                </div>
                {desk.description && (
                  <p style={{ margin: '0.25rem 0 0', color: 'var(--color-subtext0)', fontSize: '0.875rem' }}>
                    {desk.description}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  // Re-fetch latest desk data before opening manage modal
                  setManagingDesk(desk);
                }}>
                  Управление
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditDesk(desk)}>
                  Изменить
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => setDeleteDesk(desk)}>
                  Удалить
                </button>
              </div>
            </div>

            {/* Wallets */}
            <div style={{ marginTop: '1rem', display: 'flex', gap: '1.5rem' }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-subtext0)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Кошельки ({desk.wallets?.length ?? 0})
                </p>
                {desk.wallets?.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {desk.wallets.map((w: CashDeskWallet) => (
                      <span key={w.id} className="badge badge-neutral" style={{ fontSize: '0.8rem' }}>
                        {w.name} · {w.currencyCode}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: 'var(--color-overlay1)', fontSize: '0.85rem' }}>Нет кошельков</span>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-subtext0)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Пользователи ({desk.users?.length ?? 0})
                </p>
                {desk.users?.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {desk.users.map((u: CashDeskUser) => (
                      <span key={u.id} className="badge badge-blue" style={{ fontSize: '0.8rem' }}>
                        {u.fullName} ({u.role})
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: 'var(--color-overlay1)', fontSize: '0.85rem' }}>Нет пользователей</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); setNewDeskName(''); setNewDeskDesc(''); }} title="Новая группа касс">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Название *</label>
            <input
              className="form-input"
              value={newDeskName}
              onChange={e => setNewDeskName(e.target.value)}
              placeholder="Касса Main / Касса Cash"
              id="cash-desk-name-input"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Описание</label>
            <input
              className="form-input"
              value={newDeskDesc}
              onChange={e => setNewDeskDesc(e.target.value)}
              placeholder="Опционально"
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Отмена</button>
            <button
              className="btn btn-primary"
              disabled={!newDeskName.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate({ name: newDeskName.trim(), description: newDeskDesc.trim() || undefined })}
            >
              {createMutation.isPending ? 'Создание...' : 'Создать'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit modal */}
      {editDesk && (
        <Modal isOpen={!!editDesk} onClose={() => setEditDesk(null)} title={`Изменить: ${editDesk.name}`}>
          <EditDeskForm
            desk={editDesk}
            onSave={(input) => updateMutation.mutate({ id: editDesk.id, input })}
            onClose={() => setEditDesk(null)}
            isPending={updateMutation.isPending}
          />
        </Modal>
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={!!deleteDesk}
        title={`Удалить "${deleteDesk?.name}"?`}
        message="Кошельки и пользователи будут откреплены. Транзакции не затронуты."
        onConfirm={() => deleteDesk && deleteMutation.mutate(deleteDesk.id)}
        onClose={() => setDeleteDesk(null)}
        confirmText="Удалить"
        confirmVariant="danger"
      />

      {/* Manage wallets & users modal */}
      {managingDesk && (
        <Modal isOpen={!!managingDesk} onClose={() => setManagingDesk(null)} title={`Управление: ${managingDesk.name}`}>
          <div style={{ display: 'flex', gap: '2rem' }}>
            {/* Wallets */}
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 0.75rem', color: 'var(--color-text)' }}>Кошельки</h4>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: 'var(--color-subtext0)' }}>
                Только включённые кошельки видны кассирам группы
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '300px', overflowY: 'auto' }}>
                {allWallets.map((w: any) => {
                  const isAssigned = assignedWalletIds.has(w.id);
                  const otherDesks = (w.cashDeskIds ?? [])
                    .filter((id: string) => id !== managingDesk.id)
                    .map((id: string) => desks.find((d: CashDesk) => d.id === id))
                    .filter(Boolean);
                  const hasNoGroup = !w.cashDeskIds?.length && !isAssigned;
                  return (
                    <label key={w.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        disabled={assignWalletMutation.isPending}
                        onChange={(e) => assignWalletMutation.mutate({
                          cashDeskId: managingDesk.id,
                          walletId: w.id,
                          assign: e.target.checked,
                        })}
                      />
                      <span style={{ fontSize: '0.875rem' }}>
                        {w.name} <span style={{ color: 'var(--color-subtext0)' }}>({w.currencyCode})</span>
                      </span>
                      {otherDesks.length > 0 && otherDesks.map((d: CashDesk) => (
                        <span key={d.id} style={{ fontSize: '0.7rem', color: 'var(--color-blue)', background: 'var(--color-surface1)', borderRadius: '4px', padding: '0 4px' }}>{d.name}</span>
                      ))}
                      {hasNoGroup && <span style={{ fontSize: '0.75rem', color: 'var(--color-overlay1)' }}>без группы</span>}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Users */}
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 0.75rem', color: 'var(--color-text)' }}>Пользователи</h4>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: 'var(--color-subtext0)' }}>
                Только кассиры — прочие роли не имеют ограничений по кассе
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '300px', overflowY: 'auto' }}>
                {allUsers.filter((u: any) => u.role === 'cashier').map((u: any) => {
                  const isAssigned = assignedUserIds.has(u.id);
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        disabled={assignUserMutation.isPending}
                        onChange={(e) => assignUserMutation.mutate({
                          userId: u.id,
                          cashDeskId: e.target.checked ? managingDesk.id : null,
                        })}
                      />
                      <span style={{ fontSize: '0.875rem' }}>{u.fullName} <span style={{ color: 'var(--color-subtext0)' }}>@{u.username}</span></span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '1rem', textAlign: 'right' }}>
            <button className="btn btn-secondary" onClick={() => setManagingDesk(null)}>Закрыть</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EditDeskForm({ desk, onSave, onClose, isPending }: {
  desk: CashDesk;
  onSave: (input: { name?: string; description?: string | null; isActive?: boolean }) => void;
  onClose: () => void;
  isPending: boolean;
}) {
  const [name, setName] = useState(desk.name);
  const [description, setDescription] = useState(desk.description ?? '');
  const [isActive, setIsActive] = useState(desk.isActive);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div className="form-group">
        <label className="form-label">Название</label>
        <input className="form-input" value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Описание</label>
        <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
        <span>Активна</span>
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
        <button
          className="btn btn-primary"
          disabled={!name.trim() || isPending}
          onClick={() => onSave({
            name: name.trim() !== desk.name ? name.trim() : undefined,
            description: description !== (desk.description ?? '') ? (description || null) : undefined,
            isActive: isActive !== desk.isActive ? isActive : undefined,
          })}
        >
          {isPending ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
